import { useState, useEffect, useCallback } from 'react';
import { StatKey, DEFAULT_STATS } from './useAnalytics';

const STORAGE_KEY = 'blazepos-dashboard-preferences';

export type RetailWidgetKey =
  | 'revenue'
  | 'gross_profit'
  | 'sale_count'
  | 'sales_with_customer_details'
  | 'avg_items_per_sale'
  | 'avg_sale_value'
  | 'discounted'
  | 'my_sales_performance'
  | 'top_products_sold'
  | 'top_sales_people';

export const DEFAULT_ENABLED_WIDGETS: RetailWidgetKey[] = [
  'gross_profit',
  'my_sales_performance',
  'top_products_sold',
];

interface DashboardPreferences {
  selectedStats: StatKey[];
  showGlobalView: boolean;
  enabledWidgets: RetailWidgetKey[];
}

interface StoredPreferences {
  [userId: string]: DashboardPreferences;
}

const DEFAULT_PREFERENCES: DashboardPreferences = {
  selectedStats: DEFAULT_STATS,
  showGlobalView: false,
  enabledWidgets: DEFAULT_ENABLED_WIDGETS,
};

interface UseDashboardPreferencesOptions {
  userId?: string;
  defaultShowGlobalView?: boolean;
}

export function useDashboardPreferences(options: UseDashboardPreferencesOptions | string = 'anonymous') {
  // Support both old string-only API and new options object
  const { userId = 'anonymous', defaultShowGlobalView = false } = 
    typeof options === 'string' 
      ? { userId: options, defaultShowGlobalView: false } 
      : options;
  
  const [preferences, setPreferencesState] = useState<DashboardPreferences>(() => {
    // Load from localStorage on initial mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: StoredPreferences = JSON.parse(stored);
        if (parsed[userId]) {
          return {
            ...DEFAULT_PREFERENCES,
            ...parsed[userId],
          };
        }
      }
    } catch (e) {
      console.error('Failed to load dashboard preferences:', e);
    }
    // Use defaultShowGlobalView when no stored preference exists
    return { ...DEFAULT_PREFERENCES, showGlobalView: defaultShowGlobalView };
  });

  // Update state when user changes
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: StoredPreferences = JSON.parse(stored);
        if (parsed[userId]) {
          setPreferencesState({
            ...DEFAULT_PREFERENCES,
            ...parsed[userId],
          });
          return;
        }
      }
      // Use defaultShowGlobalView when no stored preference exists
      setPreferencesState({ ...DEFAULT_PREFERENCES, showGlobalView: defaultShowGlobalView });
    } catch (e) {
      console.error('Failed to load dashboard preferences:', e);
      setPreferencesState({ ...DEFAULT_PREFERENCES, showGlobalView: defaultShowGlobalView });
    }
  }, [userId, defaultShowGlobalView]);

  // Persist preferences to localStorage
  const persistPreferences = useCallback((newPrefs: DashboardPreferences) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const existing: StoredPreferences = stored ? JSON.parse(stored) : {};
      existing[userId] = newPrefs;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
    } catch (e) {
      console.error('Failed to save dashboard preferences:', e);
    }
  }, [userId]);

  // Update selected stats
  const setSelectedStats = useCallback((stats: StatKey[]) => {
    // Ensure we always have exactly 4 stats
    const validStats = stats.slice(0, 4);
    while (validStats.length < 4) {
      // Fill with defaults if needed
      const missing = DEFAULT_STATS.find(s => !validStats.includes(s));
      if (missing) {
        validStats.push(missing);
      } else {
        break;
      }
    }
    
    const newPrefs = { ...preferences, selectedStats: validStats };
    setPreferencesState(newPrefs);
    persistPreferences(newPrefs);
  }, [preferences, persistPreferences]);

  // Toggle a single stat
  const toggleStat = useCallback((stat: StatKey) => {
    const current = preferences.selectedStats;
    const isSelected = current.includes(stat);
    
    if (isSelected) {
      // Can't deselect if we only have 4
      if (current.length <= 4) {
        return;
      }
      setSelectedStats(current.filter(s => s !== stat));
    } else {
      // Add it (will be trimmed to 4 if needed)
      if (current.length >= 4) {
        // Replace the last one
        setSelectedStats([...current.slice(0, 3), stat]);
      } else {
        setSelectedStats([...current, stat]);
      }
    }
  }, [preferences.selectedStats, setSelectedStats]);

  // Reorder stats
  const reorderStats = useCallback((fromIndex: number, toIndex: number) => {
    const newStats = [...preferences.selectedStats];
    const [removed] = newStats.splice(fromIndex, 1);
    newStats.splice(toIndex, 0, removed);
    setSelectedStats(newStats);
  }, [preferences.selectedStats, setSelectedStats]);

  // Toggle global view
  const setShowGlobalView = useCallback((show: boolean) => {
    const newPrefs = { ...preferences, showGlobalView: show };
    setPreferencesState(newPrefs);
    persistPreferences(newPrefs);
  }, [preferences, persistPreferences]);

  // Update enabled retail metric widgets
  const setEnabledWidgets = useCallback((widgets: RetailWidgetKey[]) => {
    const newPrefs = { ...preferences, enabledWidgets: widgets };
    setPreferencesState(newPrefs);
    persistPreferences(newPrefs);
  }, [preferences, persistPreferences]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setPreferencesState(DEFAULT_PREFERENCES);
    persistPreferences(DEFAULT_PREFERENCES);
  }, [persistPreferences]);

  return {
    selectedStats: preferences.selectedStats,
    showGlobalView: preferences.showGlobalView,
    enabledWidgets: preferences.enabledWidgets,
    setSelectedStats,
    toggleStat,
    reorderStats,
    setShowGlobalView,
    setEnabledWidgets,
    resetToDefaults,
  };
}

